'use client'

import { useIntl } from 'react-intl'
import {
  BottomSheet,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { InfoCircle16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  RefundEstimateModel,
  ReturnShippingOptionModel,
  ReturnShippingOption,
  formatCurrencyAmount,
} from '@marketplace-web/complaints/complaints-data'

type Props = {
  note?: string
  refundEstimate?: RefundEstimateModel | null
  isOpen: boolean
  isBundleComplaint?: boolean
  isSellerBusinessUser?: boolean
  selectedShippingOption?: ReturnShippingOption
  integratedReturnShippingOption?: ReturnShippingOptionModel
  isOpenedInDialog?: boolean
  onClose: () => void
  onBuyerProtectionFeeInfoClick: () => void
  onItemDiscountsInfoClick: () => void
  onCurrencyConversionInfoClick: () => void
}

const EstimatedRefundModal = ({
  note,
  refundEstimate,
  isOpen,
  onClose,
  onBuyerProtectionFeeInfoClick,
  onCurrencyConversionInfoClick,
  onItemDiscountsInfoClick,
  isBundleComplaint = true,
  isSellerBusinessUser = false,
  isOpenedInDialog = false,
  selectedShippingOption,
  integratedReturnShippingOption,
}: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('estimated_refund_dialog')
  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const buyerProtectionFeeTranslationKey = isSellerBusinessUser
    ? 'buyer_protection_pro_fee'
    : 'buyer_protection_fee'

  const isIntegratedReturnShippingOptionSelected =
    selectedShippingOption === ReturnShippingOption.Integrated

  const renderNavigation = () => {
    return (
      <Navigation
        body={translate(selectedShippingOption ? 'confirmed_refund_title' : 'title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={onClose}
            aria={{ 'aria-label': translate('a11y.close') }}
          />
        }
      />
    )
  }

  const renderOrderItemsInfo = () => {
    if (!refundEstimate?.details.items) return null

    return (
      <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
        {refundEstimate?.details.items.map((item, index) => {
          return (
            <Cell
              styling={Cell.Styling.Tight}
              key={`${item.title}-${index}`}
              body={item.title}
              suffix={formatCurrencyAmount(item.original, locale)}
            />
          )
        })}
      </SeparatedList>
    )
  }

  const renderBuyerProtectionFee = () => {
    if (!refundEstimate?.details.buyerProtectionFee) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={
          <div className="u-flexbox">
            <Text as="span" text={translate(`${buyerProtectionFeeTranslationKey}_refund`)} />
            {isBundleComplaint && (
              <>
                <Spacer orientation={Spacer.Orientation.Vertical} />
                <button
                  className="u-flexbox u-align-self-center"
                  type="button"
                  onClick={onBuyerProtectionFeeInfoClick}
                  aria-label={translate(`a11y.${buyerProtectionFeeTranslationKey}_info_icon`)}
                >
                  <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
                </button>
              </>
            )}
          </div>
        }
        suffix={formatCurrencyAmount(refundEstimate?.details.buyerProtectionFee, locale)}
      />
    )
  }

  const renderDiscount = () => {
    if (!refundEstimate?.details.totalItemsDiscount) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={
          <div className="u-flexbox">
            <Text as="span" text={translate('item_discounts')} />
            <Spacer orientation={Spacer.Orientation.Vertical} />
            <button
              className="u-flexbox u-align-self-center"
              type="button"
              onClick={onItemDiscountsInfoClick}
              aria-label={translate('a11y.item_discounts_info_icon')}
            >
              <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
            </button>
          </div>
        }
        suffix={formatCurrencyAmount(refundEstimate?.details.totalItemsDiscount, locale, {
          formatAsNegative: true,
        })}
      />
    )
  }

  const renderAuthenticityCheckFee = () => {
    if (!refundEstimate?.details.authenticityCheckFee) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={translate('authenticity_check_fee_refund')}
        suffix={formatCurrencyAmount(refundEstimate?.details.authenticityCheckFee, locale)}
      />
    )
  }

  const renderElectronicsVerificationFee = () => {
    if (!refundEstimate?.details.electronicsVerificationFee) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={translate('electronics_check_fee_refund')}
        suffix={formatCurrencyAmount(refundEstimate?.details.electronicsVerificationFee, locale)}
      />
    )
  }

  const renderPostageRefund = () => {
    if (
      !refundEstimate?.details.shipment ||
      parseFloat(refundEstimate.details.shipment.amount) === 0
    )
      return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={translate('postage_refund')}
        suffix={formatCurrencyAmount(refundEstimate?.details.shipment, locale)}
      />
    )
  }

  const renderReturnPostagePrice = () => {
    if (!refundEstimate?.details.returnShipment) return null
    if (parseFloat(refundEstimate?.details.shipment.amount) === 0) return null
    if (!selectedShippingOption) return null
    if (!isIntegratedReturnShippingOptionSelected) return null
    if (
      isIntegratedReturnShippingOptionSelected &&
      integratedReturnShippingOption?.sellerPaidForReturn
    )
      return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={translate('return_shipping')}
        suffix={formatCurrencyAmount(refundEstimate?.details.returnShipment, locale, {
          formatAsNegative: true,
        })}
      />
    )
  }

  const renderCurrencyConvertionNote = () => {
    if (!refundEstimate?.beforeConversion) return null

    return (
      <>
        <button type="button" onClick={onCurrencyConversionInfoClick}>
          <Text
            as="h4"
            clickable
            theme="inverse"
            type={Text.Type.Caption}
            text={translate('currency_conversion_details.learn_more_link')}
          />
        </button>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h4"
          type={Text.Type.Caption}
          text={translate('currency_conversion_details.learn_more_about')}
        />
      </>
    )
  }

  const getTotalRefundPrice = () => {
    if (!refundEstimate?.totalEstimatedRefund) return null

    if (
      isIntegratedReturnShippingOptionSelected &&
      !integratedReturnShippingOption?.sellerPaidForReturn &&
      refundEstimate?.estimatedRefundWithoutShipment
    )
      return formatCurrencyAmount(refundEstimate.estimatedRefundWithoutShipment, locale)

    return formatCurrencyAmount(refundEstimate?.totalEstimatedRefund, locale)
  }

  const renderSalesTax = () => {
    if (!refundEstimate?.details.salesTax) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={translate('sales_tax')}
        suffix={formatCurrencyAmount(refundEstimate?.details.salesTax, locale)}
      />
    )
  }

  const renderTotalRefund = () => {
    return (
      <>
        <Cell
          styling={Cell.Styling.Tight}
          title={translate(selectedShippingOption ? 'confirmed_total_refund' : 'total')}
          suffix={<Text as="h2" type={Text.Type.Title} text={getTotalRefundPrice()} />}
        />
        {renderCurrencyConvertionNote()}
      </>
    )
  }

  const renderNote = () => {
    if (!note) return null

    return (
      <Cell
        styling={Cell.Styling.Tight}
        body={<Text as="h4" type={Text.Type.Caption} text={note} />}
      />
    )
  }

  const renderActions = () => {
    return (
      <Cell
        body={<Button text={translate('ok')} styling={Button.Styling.Filled} onClick={onClose} />}
      />
    )
  }

  const renderContent = () => {
    return (
      <div className="u-fill-width">
        {renderNavigation()}
        <Divider />
        <ScrollableArea>
          <Cell>
            <SeparatedList separator={<Spacer size={Spacer.Size.Large} />}>
              {renderOrderItemsInfo()}
              {renderDiscount()}
              {renderBuyerProtectionFee()}
              {renderAuthenticityCheckFee()}
              {renderElectronicsVerificationFee()}
              {renderPostageRefund()}
              {renderReturnPostagePrice()}
              {renderSalesTax()}
              {renderTotalRefund()}
              {renderNote()}
            </SeparatedList>
          </Cell>
          {!isMobileView && renderActions()}
        </ScrollableArea>
      </div>
    )
  }

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog hasScrollableContent show={isOpen} defaultCallback={onClose} closeOnOverlay>
      {renderContent()}
    </Dialog>
  )
}

export default EstimatedRefundModal
